import { RiLockPasswordFill } from 'react-icons/ri';
import { FaUserEdit } from 'react-icons/fa';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import {
   usePasswordChangeMutation,
   useGetUserByIdQuery,
   useUpdateUserMutation,
} from '../Redux/Service/UserApi';
import { useGetDepartmentsQuery } from '../Redux/Service/Department';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import StyledDashboardLayout from './style';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Grid, FormHelperText } from '@mui/material';
import { API_URL } from '../Config';

function ProfileSettings() {
   const [passwordChange] = usePasswordChangeMutation();
   const [updateUser] = useUpdateUserMutation();

   const [showOldPass, setShowOldPass] = useState(false);
   const [showNewPass, setShowNewPass] = useState(false);
   const [showConfirmPass, setShowConfirmPass] = useState(false);
   const [isEditing, setIsEditing] = useState(false);
   const [departments, setDepartments] = useState(null);

   // ── Signature upload state ──────────────────────────────────────────
   // existingSignatureUrl: derived once from the saved user record
   // newSignaturePreview: local blob preview of a freshly picked file
   const [existingSignatureUrl, setExistingSignatureUrl] = useState(null);
   const [newSignaturePreview, setNewSignaturePreview] = useState(null);
   const [signatureFile, setSignatureFile] = useState(null);
   const [signatureError, setSignatureError] = useState(null);
   const fileRef = useRef();

   const userState = useSelector((state) => state.authState?.user);
   const userId = userState?.id;

   const { data: currentUserData, refetch: refetchUserData } = useGetUserByIdQuery(userId, {
      skip: !userId,
   });

   const {
      data: departmentsData,
      isLoading: isLoadingForDepartment,
      isError: isErrorForDepartment,
   } = useGetDepartmentsQuery();

   useEffect(() => {
      if (!isLoadingForDepartment && !isErrorForDepartment) {
         setDepartments(departmentsData?.response);
      }
   }, [isLoadingForDepartment, isErrorForDepartment, departmentsData]);

   const user = currentUserData?.response;

   // Build the existing signature's full URL whenever the user record changes
   useEffect(() => {
      if (user?.signature) {
         const isFullUrl = /^https?:\/\//i.test(user.signature);
         setExistingSignatureUrl(
            isFullUrl
               ? user.signature
               : `${API_URL}${user.signature?.split('/').slice(3).join('/')}`,
         );
      } else {
         setExistingSignatureUrl(null);
      }
   }, [user]);

   // ── Profile validation (same rules as UserAdd, email/role excluded) ─────
   const profileValidationSchema = Yup.object({
      name: Yup.string()
         .matches(/^[a-zA-Z\s]+$/, 'Name can only contain letters and spaces')
         .matches(/^[a-zA-Z]/, 'Name cannot start with a space')
         .max(50, 'Name should be atmost 50 characters')
         .min(2, 'Name must be at least 2 characters')
         .required('Name is required'),
      phoneNo: Yup.string()
         .matches(
            /^$|^(\+\d{1,3})?\d{10}$/,
            'Phone number must be 10 digits, optionally prefixed with a country code (e.g. +911234567891)',
         )
         .notRequired(),
      departmentId: Yup.string().required('Department is required'),
   });

   const handleNameInput = (e, setFieldValue) => {
      const value = e.target.value;
      if (value.length === 1 && value === ' ') return;
      const filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
      const cleanedValue = filteredValue.replace(/\s+/g, ' ');
      setFieldValue('name', cleanedValue);
   };

   const handlePhoneInput = (e, setFieldValue) => {
      const value = e.target.value;
      const filteredValue = value.replace(/[^0-9]/g, '');
      setFieldValue('phoneNo', filteredValue);
   };

   // ── Signature file change handler ───────────────────────────────────
   const handleSignatureChange = (event) => {
      const file = event.target.files[0];
      if (!file) return;

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];

      if (!allowedTypes.includes(file.type)) {
         toast.error('Only JPG, JPEG, and PNG files are allowed');
         setSignatureError('Invalid file type. Only JPG, JPEG, and PNG files are allowed');
         event.target.value = '';
         setSignatureFile(null);
         setNewSignaturePreview(null);
         return;
      }

      setSignatureError(null);
      setSignatureFile(file);
      setNewSignaturePreview(URL.createObjectURL(file));
   };

   const handleProfileSubmit = async (values, { setSubmitting }) => {
      try {
         setSubmitting(true);
         const formData = new FormData();
         formData.append('id', userId);
         formData.append('name', values.name);
         formData.append('email', user?.email || ''); // required by backend validation, kept unchanged
         formData.append('phoneNo', values.phoneNo || '');
         formData.append('departmentId', parseInt(values.departmentId));

         // Signature is optional — only append if the user picked a new file
         if (signatureFile) {
            formData.append('signature', signatureFile);
         }

         const updateUserData = await updateUser(formData);

         if (updateUserData?.error) {
            toast.error(updateUserData?.error?.data?.message || 'Failed to update profile');
         } else {
            toast.success(updateUserData?.data?.message || 'Profile updated successfully');
            setIsEditing(false);
            setSignatureFile(null);
            setNewSignaturePreview(null);
            refetchUserData();
         }
      } catch (err) {
         toast.error('Something went wrong, Please try again later!');
      } finally {
         setSubmitting(false);
      }
   };

   // ── Password validation ──────────────────────────────────────────────
   const passwordValidationSchema = Yup.object().shape({
      oldPassword: Yup.string().required('Current password is required'),
      newPassword: Yup.string()
         .required('New password is required')
         .min(6, 'Password must be at least 6 characters long')
         .matches(
            /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d@]{6,}$/,
            'Password must contain at least one letter and one number',
         )
         .notOneOf([Yup.ref('oldPassword')], 'New password must be different from current password')
         .max(10, 'Password should be atmost 10 characters'),
      confirmPassword: Yup.string()
         .required('Please confirm your new password')
         .oneOf([Yup.ref('newPassword')], 'Passwords do not match')
         .max(10, 'Password should be atmost 10 characters'),
   });

   const handlePasswordSubmit = async (values, { resetForm, setSubmitting }) => {
      try {
         setSubmitting(true);
         const result = await passwordChange({
            oldPassword: values.oldPassword,
            newPassword: values.newPassword,
         });
         if (result?.error) {
            toast.error(
               result.error?.data?.message ||
                  result.error?.data?.error ||
                  'Failed to update password',
            );
         } else {
            toast.success(result?.data?.message || 'Password updated successfully');
            resetForm();
         }
      } catch (err) {
         toast.error('Failed to update password');
      } finally {
         setSubmitting(false);
      }
   };

   const removeLogo = (setFieldValue) => {
      fileRef.current.value = null;
      setFieldValue('signature', null);
      setSignatureFile(null);
      setNewSignaturePreview(null);
      setExistingSignatureUrl(null);
   };

   return (
      <StyledDashboardLayout>
         <div className="content-wrapper">
            <div className="content-header">
               <div className="container-fluid">
                  <div className="row mb-2">
                     <div className="col-sm-6 pageTitle">
                        <h1 className="m-0">Profile Settings</h1>
                        <p className="text-muted mb-0">
                           Manage your personal information and security settings
                        </p>
                     </div>
                  </div>
               </div>
            </div>

            <div className="container-fluid">
               <Grid container spacing={3} className="m-3" sx={{ width: 'auto' }}>
                  {/* ── PROFILE INFORMATION ─────────────────────────────── */}
                  <Grid item md={6} xs={12}>
                     <div className="card customCard h-100">
                        <div className="card-header d-flex justify-content-between align-items-center">
                           <h3 className="card-title mb-0">
                              <FaUserEdit className="icon-size" /> Profile Information
                           </h3>
                           {!isEditing && (
                              <button
                                 type="button"
                                 className="btn btn-sm commonBtn"
                                 style={{
                                    padding: '5px 19px',
                                    fontSize: '15px',
                                    marginLeft: 'auto',
                                 }}
                                 onClick={() => setIsEditing(true)}
                              >
                                 Edit
                              </button>
                           )}
                        </div>

                        <Formik
                           enableReinitialize
                           initialValues={{
                              name: user?.name || '',
                              phoneNo: user?.phoneNo || '',
                              departmentId: user?.department?.id ? String(user.department.id) : '',
                           }}
                           validationSchema={profileValidationSchema}
                           onSubmit={handleProfileSubmit}
                        >
                           {({
                              setFieldValue,
                              values,
                              touched,
                              errors,
                              isSubmitting,
                              resetForm,
                           }) => (
                              <Form autoComplete="off">
                                 <div className="card-body">
                                    <div className="form-group">
                                       <label htmlFor="name">Full Name *</label>
                                       <Field
                                          type="text"
                                          name="name"
                                          autoComplete="off"
                                          className="form-control"
                                          value={values.name}
                                          onChange={(e) => handleNameInput(e, setFieldValue)}
                                          placeholder="Enter full name"
                                          maxLength={50}
                                          disabled={!isEditing || isSubmitting}
                                       />
                                       {touched.name && (
                                          <FormHelperText
                                             sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                          >
                                             {errors.name}
                                          </FormHelperText>
                                       )}
                                    </div>

                                    <div className="form-group">
                                       <label htmlFor="email">Email Address *</label>
                                       <input
                                          type="email"
                                          className="form-control"
                                          value={user?.email || ''}
                                          disabled
                                       />
                                       <small className="form-text text-muted">
                                          Email address cannot be changed
                                       </small>
                                    </div>

                                    <div className="form-group">
                                       <label htmlFor="phoneNo">Phone Number</label>
                                       <Field
                                          type="text"
                                          name="phoneNo"
                                          autoComplete="off"
                                          className="form-control"
                                          value={values.phoneNo}
                                          onChange={(e) => handlePhoneInput(e, setFieldValue)}
                                          placeholder="Enter phone number"
                                          helperText="e.g 9054678910"
                                          maxLength={15}
                                          disabled={!isEditing || isSubmitting}
                                       />
                                       {touched.phoneNo && (
                                          <FormHelperText
                                             sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                          >
                                             {errors.phoneNo}
                                          </FormHelperText>
                                       )}
                                    </div>

                                    <div className="form-group">
                                       <label htmlFor="departmentId">Department *</label>
                                       <Field
                                          as="select"
                                          name="departmentId"
                                          className="form-control"
                                          disabled={!isEditing || isSubmitting}
                                       >
                                          <option value="">Select Department</option>
                                          {departments?.map((val) => (
                                             <option key={val.id} value={val.id}>
                                                {val.name}
                                             </option>
                                          ))}
                                       </Field>
                                       {touched.departmentId && (
                                          <FormHelperText
                                             sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                          >
                                             {errors.departmentId}
                                          </FormHelperText>
                                       )}
                                    </div>

                                    {/* ── Signature Upload (Optional) ─────────────────── */}
                                    <div className="form-group">
                                       <label htmlFor="signature">Signature (Optional)</label>
                                       <div>
                                          {(newSignaturePreview || existingSignatureUrl) && (
                                             <>
                                                <div style={{ marginBottom: '8px' }}>
                                                   <img
                                                      ref={fileRef}
                                                      src={
                                                         newSignaturePreview || existingSignatureUrl
                                                      }
                                                      alt="Signature Preview"
                                                      style={{
                                                         width: '150px',
                                                         height: '80px',
                                                         objectFit: 'contain',
                                                         border: '1px solid #ccc',
                                                         borderRadius: '4px',
                                                         padding: '4px',
                                                      }}
                                                   />
                                                </div>
                                                <button
                                                   type="button"
                                                   onClick={() => removeLogo(setFieldValue)}
                                                   disabled={!isEditing}
                                                   style={{
                                                      padding: '5px 10px',
                                                      border: 'none',
                                                      fontSize: '14px',
                                                      backgroundColor: 'red',
                                                      color: '#fff',
                                                      borderRadius: '7px',
                                                      marginBottom: '20px',
                                                      ...(!isEditing && { opacity: '.5' }),
                                                   }}
                                                >
                                                   Remove Logo
                                                </button>
                                             </>
                                          )}
                                          <input
                                             id="signature"
                                             type="file"
                                             accept="image/jpeg,image/jpg,image/png"
                                             className="form-control"
                                             onChange={handleSignatureChange}
                                             disabled={!isEditing || isSubmitting}
                                          />
                                          <small className="form-text text-muted">
                                             Only JPG, JPEG, and PNG files are allowed
                                          </small>
                                          {signatureError && (
                                             <FormHelperText
                                                sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                             >
                                                {signatureError}
                                             </FormHelperText>
                                          )}
                                       </div>
                                    </div>

                                    <div className="form-group">
                                       <label>Role</label>
                                       <div>
                                          {user?.role ? (
                                             <span className="badge badge-role mr-2">
                                                {user?.role?.name}
                                             </span>
                                          ) : (
                                             <span>N/A</span>
                                          )}
                                       </div>
                                       {/* <small className="form-text text-muted">
                                          Role cannot be changed from this page
                                       </small> */}
                                    </div>

                                    <div className="form-group">
                                       <label>Status</label>
                                       <div>
                                          <span
                                             className={`badge ${
                                                user?.status ? 'badge-active' : 'badge-inactive'
                                             }`}
                                          >
                                             {user?.status ? 'Active' : 'Inactive'}
                                          </span>
                                       </div>
                                    </div>
                                 </div>

                                 {isEditing && (
                                    <div className="card-footer d-flex gap-2">
                                       <button
                                          type="button"
                                          className="btn btn-outline-secondary"
                                          disabled={isSubmitting}
                                          onClick={() => {
                                             resetForm();
                                             setIsEditing(false);
                                             setSignatureFile(null);
                                             setSignatureError(null);
                                             setNewSignaturePreview(null);
                                          }}
                                       >
                                          Cancel
                                       </button>
                                       <button
                                          type="submit"
                                          className="btn commonBtn"
                                          disabled={isSubmitting}
                                          style={{ marginLeft: '10px' }}
                                       >
                                          {isSubmitting ? (
                                             <>
                                                <span
                                                   className="spinner-border spinner-border-sm mr-2"
                                                   role="status"
                                                   aria-hidden="true"
                                                ></span>
                                                Saving...
                                             </>
                                          ) : (
                                             'Save Changes'
                                          )}
                                       </button>
                                    </div>
                                 )}
                              </Form>
                           )}
                        </Formik>
                     </div>
                  </Grid>

                  {/* ── CHANGE PASSWORD ─────────────────────────────────── */}
                  <Grid item md={6} xs={12}>
                     <div className="card customCard h-100">
                        <div className="card-header">
                           <h3 className="card-title mb-0">
                              <RiLockPasswordFill className="icon-size" /> Change Password
                           </h3>
                        </div>

                        <Formik
                           initialValues={{
                              oldPassword: '',
                              newPassword: '',
                              confirmPassword: '',
                           }}
                           validationSchema={passwordValidationSchema}
                           onSubmit={handlePasswordSubmit}
                        >
                           {({ touched, errors, isSubmitting }) => (
                              <Form autoComplete="off">
                                 <div className="card-body">
                                    <div className="form-group">
                                       <label htmlFor="oldPassword">Current Password</label>
                                       <div style={{ position: 'relative' }}>
                                          <Field
                                             type={showOldPass ? 'text' : 'password'}
                                             name="oldPassword"
                                             className="form-control"
                                             id="oldPassword"
                                             placeholder="Enter current password"
                                             disabled={isSubmitting}
                                          />
                                          <span
                                             className="password-toggle-icon"
                                             style={{
                                                position: 'absolute',
                                                right: '12px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                cursor: 'pointer',
                                                color: '#6c757d',
                                             }}
                                             onClick={() => setShowOldPass((prev) => !prev)}
                                          >
                                             {showOldPass ? <FaEyeSlash /> : <FaEye />}
                                          </span>
                                       </div>
                                       {touched.oldPassword && (
                                          <FormHelperText
                                             sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                          >
                                             {errors.oldPassword}
                                          </FormHelperText>
                                       )}
                                    </div>

                                    <div className="form-group">
                                       <label htmlFor="newPassword">New Password</label>
                                       <div style={{ position: 'relative' }}>
                                          <Field
                                             type={showNewPass ? 'text' : 'password'}
                                             name="newPassword"
                                             className="form-control"
                                             id="newPassword"
                                             placeholder="Enter new password"
                                             disabled={isSubmitting}
                                          />
                                          <span
                                             className="password-toggle-icon"
                                             style={{
                                                position: 'absolute',
                                                right: '12px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                cursor: 'pointer',
                                                color: '#6c757d',
                                             }}
                                             onClick={() => setShowNewPass((prev) => !prev)}
                                          >
                                             {showNewPass ? <FaEyeSlash /> : <FaEye />}
                                          </span>
                                       </div>
                                       {touched.newPassword && (
                                          <FormHelperText
                                             sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                          >
                                             {errors.newPassword}
                                          </FormHelperText>
                                       )}
                                    </div>

                                    <div className="form-group">
                                       <label htmlFor="confirmPassword">Confirm New Password</label>
                                       <div style={{ position: 'relative' }}>
                                          <Field
                                             type={showConfirmPass ? 'text' : 'password'}
                                             name="confirmPassword"
                                             className="form-control"
                                             id="confirmPassword"
                                             placeholder="Confirm new password"
                                             disabled={isSubmitting}
                                          />
                                          <span
                                             className="password-toggle-icon"
                                             style={{
                                                position: 'absolute',
                                                right: '12px',
                                                top: '50%',
                                                transform: 'translateY(-50%)',
                                                cursor: 'pointer',
                                                color: '#6c757d',
                                             }}
                                             onClick={() => setShowConfirmPass((prev) => !prev)}
                                          >
                                             {showConfirmPass ? <FaEyeSlash /> : <FaEye />}
                                          </span>
                                       </div>
                                       {touched.confirmPassword && (
                                          <FormHelperText
                                             sx={{ color: '#d32f2f', margin: '3px 14px' }}
                                          >
                                             {errors.confirmPassword}
                                          </FormHelperText>
                                       )}
                                    </div>

                                    <div className="password-requirements-box p-3 mb-3">
                                       <strong className="d-block mb-2">
                                          Password Requirements
                                       </strong>
                                       <ul className="mb-0 pl-3">
                                          <li>At least 6 characters long</li>
                                          <li>Must include at least one letter and one number</li>
                                          <li>@ symbol is allowed</li>
                                       </ul>
                                    </div>
                                 </div>

                                 <div className="card-footer">
                                    <button
                                       type="submit"
                                       className="btn commonBtn"
                                       disabled={isSubmitting}
                                    >
                                       {isSubmitting ? (
                                          <>
                                             <span
                                                className="spinner-border spinner-border-sm mr-2"
                                                role="status"
                                                aria-hidden="true"
                                             ></span>
                                             Updating...
                                          </>
                                       ) : (
                                          'Update Password'
                                       )}
                                    </button>
                                 </div>
                              </Form>
                           )}
                        </Formik>
                     </div>
                  </Grid>
               </Grid>
            </div>
         </div>
      </StyledDashboardLayout>
   );
}

export default ProfileSettings;
